import { useCurrenciesStore as useCoreCurrenciesStore, Currency } from '@flows.cash/core';
import { toast } from 'sonner';
import { useAuthStore } from './authStore';

// Re-export the Currency type from the core package
export type { Currency } from '@flows.cash/core';

// Create a web-specific wrapper around the core store
export const useCurrenciesStore = () => {
  const coreStore = useCoreCurrenciesStore();
  const { user } = useAuthStore();

  return {
    ...coreStore,

    // Web-specific wrappers with toast notifications
    fetchCurrencies: async () => {
      if (!user?.id) {
        toast.error('You must be logged in to fetch currencies');
        return;
      }

      try {
        await coreStore.fetchCurrencies(user.id);
      } catch (error) {
        console.error('Error fetching currencies:', error);
        toast.error('Failed to load currencies');
      }
    },

    addCurrency: async (currency: Omit<Currency, 'id'>) => {
      if (!user?.id) {
        toast.error('You must be logged in to add a currency');
        return;
      }

      try {
        // First check if this currency already exists for the user
        const existingCurrency = coreStore.currencies.find(
          c => c.code === currency.code && c.user_id === user.id
        );

        await coreStore.addCurrency(user.id, currency);
        
        if (existingCurrency) {
          toast.success(`Currency ${currency.code} updated successfully`);
        } else {
          toast.success(`Currency ${currency.code} added successfully`);
        }
      } catch (error) {
        console.error('Error adding/updating currency:', error);
        toast.error('Failed to add/update currency');
        throw error;
      }
    },

    updateCurrency: async (currency: Currency) => {
      if (!user?.id) {
        toast.error('You must be logged in to update a currency');
        return;
      }

      try {
        await coreStore.updateCurrency(user.id, currency);
        toast.success('Currency updated successfully');
      } catch (error) {
        console.error('Error updating currency:', error);
        toast.error('Failed to update currency');
        throw error;
      }
    },

    deleteCurrency: async (currencyId: string) => {
      if (!user?.id) {
        toast.error('You must be logged in to delete a currency');
        return;
      }

      try {
        await coreStore.deleteCurrency(user.id, currencyId);
        toast.success('Currency deleted successfully');
      } catch (error) {
        console.error('Error deleting currency:', error);
        toast.error('Failed to delete currency');
        throw error;
      }
    },

    setDefaultCurrency: async (currencyId: string) => {
      if (!user?.id) {
        toast.error('You must be logged in to set a default currency');
        return;
      }

      try {
        await coreStore.setDefaultCurrency(user.id, currencyId);
        toast.success('Default currency updated');
      } catch (error) {
        console.error('Error setting default currency:', error);
        toast.error('Failed to set default currency');
        throw error;
      }
    },

    fetchExchangeRates: async (forceApiUpdate?: boolean) => {
      try {
        console.log(`[webCurrenciesStore] Attempting to fetch exchange rates${forceApiUpdate ? ', forcing API update' : ''}`);
        
        // Track fetch attempt in analytics or logging
        const fetchStart = Date.now();
        
        // Call the core store's fetchExchangeRates
        // Exchange rates are global and not user-specific
        const rates = await coreStore.fetchExchangeRates(forceApiUpdate);
        
        const fetchTime = Date.now() - fetchStart;
        console.log(`[webCurrenciesStore] Fetch exchange rates completed in ${fetchTime}ms`);
        
        if (!rates) {
          console.warn('[webCurrenciesStore] No exchange rates returned from core store');
          // Don't show toast for no rates as this is expected on first load
        } else {
          console.log(`[webCurrenciesStore] Successfully fetched exchange rates for ${Object.keys(rates).length} currencies`);
        }
        
        return rates;
      } catch (error) {
        console.error('[webCurrenciesStore] Error fetching exchange rates:', error);
        
        // Show toast for unexpected errors
        toast.error('Failed to fetch exchange rates');
        
        return null;
      }
    },

    forceUpdateExchangeRates: async () => {
      try {
        console.log('[webCurrenciesStore] Forcing update of exchange rates directly from API');
        
        // Track fetch attempt in analytics or logging
        const fetchStart = Date.now();
        
        // Call the core store's forceUpdateExchangeRates method
        const rates = await coreStore.forceUpdateExchangeRates();
        
        const fetchTime = Date.now() - fetchStart;
        console.log(`[webCurrenciesStore] Force update of exchange rates completed in ${fetchTime}ms`);
        
        if (!rates) {
          console.warn('[webCurrenciesStore] No exchange rates returned from direct API fetch');
          toast.error('Failed to update exchange rates from API');
          return null;
        } else {
          console.log(`[webCurrenciesStore] Successfully fetched fresh exchange rates for ${Object.keys(rates).length} currencies`);
          toast.success('Exchange rates updated with latest market data');
        }
        
        return rates;
      } catch (error) {
        console.error('[webCurrenciesStore] Error forcing update of exchange rates:', error);
        toast.error('Failed to update exchange rates from API');
        return null;
      }
    },

    initializeDefaultCurrencies: async () => {
      if (!user?.id) {
        toast.error('You must be logged in to initialize currencies');
        return;
      }

      try {
        await coreStore.initializeDefaultCurrencies(user.id);
        toast.success("Default currencies initialized successfully");
      } catch (error) {
        console.error('Error initializing default currencies:', error);
        toast.error('Failed to initialize default currencies');
      }
    }
  };
};
